import { NavLink, Outlet, useLocation, useSearchParams } from 'react-router-dom'

import type { HomeView } from '../lib/homeView'
import { useAccess } from '../lib/session'
import { useHomeView } from '../lib/useHomeView'

// Drugorzędny pasek podzakładek — renderuje pigułki + <Outlet/> dla tras
// zgrupowanych pod jedną zakładką główną (np. Analiza Rywala: Drużyny / Szanse;
// Trening: Raport / Wbijanie metryk). Sam pasek jest no-print (trasy druku i tak
// żyją poza shellem). Przy włączonej fladze AUTH_REQUIRED pigułki stron bez dostępu
// znikają (sekcje: lib/access.ts).
export interface SubTab {
  to: string
  label: string
  // Prefiksy uznające podzakładkę za aktywną (domyślnie [to]) — pozwala
  // podświetlić „Drużyny" także na widoku szczegółu /dossier/:id itd.
  // Kilka pasujących = wygrywa najdłuższy prefiks (/gracze/porownanie ≠ /gracze).
  prefixes?: string[]
  // Moduł konta, w którym pigułka stoi w pasku (Sztab / Scout / Dyrektor —
  // lib/useHomeView.ts); brak = we wszystkich. Sama trasa zostaje dostępna wg
  // sekcji — to tylko układ menu, nie uprawnienie.
  views?: HomeView[]
}

export function SubTabLayout({ tabs }: { tabs: SubTab[] }) {
  const { pathname } = useLocation()
  const access = useAccess()
  const { view } = useHomeView()
  // ?widok= (przełącznik admina) jedzie z pigułkami, inaczej przejście na drugą
  // podzakładkę zrzucałoby admina z podglądanego widoku do domyślnego.
  const [params] = useSearchParams()
  const widok = params.get('widok')
  const visible = tabs.filter((t) => access.canView(t.to) && (!t.views || t.views.includes(view)))
  const matchLen = (t: SubTab) => Math.max(-1, ...(t.prefixes ?? [t.to])
    .filter((p) => pathname === p || pathname.startsWith(p + '/'))
    .map((p) => p.length))
  const best = Math.max(-1, ...visible.map(matchLen))
  return (
    <>
      <div className="no-print border-b border-ink-100 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-1.5 px-6 py-2">
          {visible.map((t) => {
            const active = best >= 0 && matchLen(t) === best
            return (
              <NavLink
                key={t.to}
                to={widok ? `${t.to}?widok=${encodeURIComponent(widok)}` : t.to}
                className={[
                  'rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-colors',
                  active
                    ? 'bg-forest-800 text-white shadow-sm'
                    : 'text-ink-500 hover:bg-ink-100 hover:text-ink-900',
                ].join(' ')}
              >
                {t.label}
              </NavLink>
            )
          })}
        </div>
      </div>
      <Outlet />
    </>
  )
}
